import axios from 'axios';
import { cloneDeep } from 'lodash-es';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { mapRecordingsForWidgetResponseDtoToModel } from '@hotjar/webapp/domains/recordings/mapper';
import {
  QueryKeyEnum,
  QueryFormatFilter,
  QueryListOperandEnum,
  QueryMapOperandEnum,
} from '@hotjar/global-filters';

import { getSiteId } from '../../../utils/getParamsFromUrlPathname';
import { getUTCOffset } from './getUTCOffset';
import { RELEVANT_RECORDINGS_VALUES } from '../../RecordingsOverview/constants';

import type { Recording } from '@hotjar/webapp/domains/recordings/types';
import type { Store } from '../../../store/list/types';

export const basePostData = {
  action: 'search',
  is_initial_load: false,
  source: 'recordings-widget',
  tz_offset: getUTCOffset(),
  offset: 0,
  size: 20,
};

const last24HoursDateRangeFilter = {
  [QueryMapOperandEnum.DAYS_AGO]: { [QueryKeyEnum.created]: 0 },
};

const last7DaysDateRangeFilter = {
  [QueryMapOperandEnum.DAYS_AGO]: { [QueryKeyEnum.created]: 7 },
};

enum RecommendedWidgetKey {
  RELEVANT = 'relevant',
  ENGAGED = 'engaged',
  FRUSTRATED = 'frustrated',
  RAGE_CLICKS = 'rage-clicks',
  UTURNS = 'uturns',
  ERRORS = 'errors',
}

export type RecommendedWidgetFetchConfig = {
  widgetKey: RecommendedWidgetKey;
  filters: QueryFormatFilter;
  sortBy: string;
  relevanceFilter?: number[];
};

export const WIDGETS_FETCH_DATA: { [key: string]: RecommendedWidgetFetchConfig } = {
  RELEVANT: {
    widgetKey: RecommendedWidgetKey.RELEVANT,
    filters: last24HoursDateRangeFilter,
    sortBy: '-relevance_score',
  },
  ENGAGED: {
    widgetKey: RecommendedWidgetKey.ENGAGED,
    filters: {
      [QueryListOperandEnum.AND]: [last7DaysDateRangeFilter],
    },
    relevanceFilter: RELEVANT_RECORDINGS_VALUES,
    sortBy: '-created',
  },
  FRUSTRATED: {
    widgetKey: RecommendedWidgetKey.FRUSTRATED,
    filters: last7DaysDateRangeFilter,
    sortBy: '-frustration_score',
  },
  RAGE_CLICKS: {
    widgetKey: RecommendedWidgetKey.RAGE_CLICKS,
    filters: {
      [QueryListOperandEnum.AND]: [
        last7DaysDateRangeFilter,
        { [QueryMapOperandEnum.EQUAL]: { [QueryKeyEnum.rageclick]: true } },
      ],
    },
    sortBy: '-created',
  },
  UTURNS: {
    widgetKey: RecommendedWidgetKey.UTURNS,
    filters: {
      [QueryListOperandEnum.AND]: [
        last7DaysDateRangeFilter,
        { [QueryMapOperandEnum.EQUAL]: { [QueryKeyEnum.uturn]: true } },
      ],
    },
    sortBy: '-created',
  },
  ERRORS: {
    widgetKey: RecommendedWidgetKey.ERRORS,
    filters: {
      [QueryListOperandEnum.AND]: [last7DaysDateRangeFilter, { [QueryKeyEnum.error]: [] }],
    },
    sortBy: '-created',
  },
};

const RECORDINGS_OVERVIEW_QUERY_KEY = 'recordings-overview';

export const fetchWidgetData = async ({
  filters,
  sortBy,
  widgetKey,
  relevanceFilter,
}: RecommendedWidgetFetchConfig) => {
  try {
    const clonedFilters = cloneDeep(filters);

    if (relevanceFilter) {
      (
        clonedFilters[QueryListOperandEnum.AND as keyof QueryFormatFilter] as QueryFormatFilter[]
      ).push({
        [QueryMapOperandEnum.EQUAL]: {
          relevance_score: relevanceFilter,
        },
      });
    }

    const siteId = getSiteId();
    const postData = {
      ...basePostData,
      sort_by: sortBy,
      global_filters: JSON.stringify(clonedFilters),
    };

    const { data: res } = await queryClient.fetchQuery(
      [RECORDINGS_OVERVIEW_QUERY_KEY, widgetKey],
      () =>
        axios
          .post(`/api/v3/sites/${siteId}/recordings`, postData)
          .catch(() => Promise.resolve({ data: { res: [] } })),
    );

    const mappedRecordings = mapRecordingsForWidgetResponseDtoToModel(res);

    return { recordings: mappedRecordings as Recording[], totalHits: res.total_hits };
  } catch (e) {
    return { recordings: [] as Recording[], totalHits: 0 };
  }
};

export const getOverviewRecordings = (store: Store): void => {
  // This is a naive and simple way of getting fixed data for the widgets.
  // If we stick to the experiment's variant we need to create a more scalable way of adding new widgets and fetching their data.
  const { setOverviewData, setWidgetsLoaded } = store.getState();

  Promise.all([
    fetchWidgetData(WIDGETS_FETCH_DATA.RELEVANT),
    fetchWidgetData(WIDGETS_FETCH_DATA.ENGAGED),
    fetchWidgetData(WIDGETS_FETCH_DATA.RAGE_CLICKS),
    fetchWidgetData(WIDGETS_FETCH_DATA.UTURNS),
    fetchWidgetData(WIDGETS_FETCH_DATA.ERRORS),
    fetchWidgetData(WIDGETS_FETCH_DATA.FRUSTRATED),
  ]).then(
    ([
      recordingsRelevant,
      recordingsEngaged,
      recordingsWithRageClicks,
      recordingsWithUturns,
      recordingsWithErrors,
      recordingsFrustrated,
    ]) => {
      setOverviewData({
        recordingsRelevant: recordingsRelevant.recordings,
        recordingsWithRageClicks: recordingsWithRageClicks.recordings,
        recordingsEngaged: recordingsEngaged.recordings,
        recordingsWithErrors: recordingsWithErrors.recordings,
        recordingsWithUturns: recordingsWithUturns.recordings,
        recordingsFrustrated: recordingsFrustrated.recordings,
        recordingsRelevantTotalHits: recordingsRelevant.totalHits,
        recordingsWithRageClicksTotalHits: recordingsWithRageClicks.totalHits,
        recordingsEngagedTotalHits: recordingsEngaged.totalHits,
        recordingsWithErrorsTotalHits: recordingsWithErrors.totalHits,
        recordingsWithUturnsTotalHits: recordingsWithUturns.totalHits,
        recordingsFrustratedTotalHits: recordingsFrustrated.totalHits,
      });

      setWidgetsLoaded(true);
    },
  );
};
